export type ConnectivityStatus =
	| "online"
	| "degraded"
	| "offline"
	| "reconnecting";

export interface ConnectivitySnapshot {
	status: ConnectivityStatus;
	sinceMs: number;
	lastOkAtMs?: number;
	lastError?: { message: string; atMs: number };
	nextRetryAtMs?: number;
	/**
	 * Incremented by `requestReconnectNow()` so the supervisor runs an immediate
	 * `health.ping` attempt (banner “Retry now”).
	 */
	reconnectKick: number;
}

type Listener = () => void;

const SERVER_SNAPSHOT: ConnectivitySnapshot = {
	status: "online",
	sinceMs: 0,
	lastOkAtMs: 0,
	reconnectKick: 0,
};

let state: ConnectivitySnapshot = {
	status: "online",
	sinceMs: Date.now(),
	lastOkAtMs: Date.now(),
	reconnectKick: 0,
};
const listeners = new Set<Listener>();

function emit() {
	for (const l of listeners) {
		try {
			l();
		} catch {
			// ignore
		}
	}
}

function setState(next: ConnectivitySnapshot) {
	state = next;
	emit();
}

function transition(status: ConnectivityStatus, reason?: string) {
	const now = Date.now();
	if (state.status === status) {
		return;
	}
	setState({
		...state,
		status,
		sinceMs: now,
		lastError: reason ? { message: reason, atMs: now } : state.lastError,
		...(status === "online"
			? { lastOkAtMs: now, nextRetryAtMs: undefined }
			: {}),
	});
}

export const connectivityStore = {
	subscribe(listener: Listener) {
		listeners.add(listener);
		return () => listeners.delete(listener);
	},
	getSnapshot(): ConnectivitySnapshot {
		return state;
	},
	getServerSnapshot(): ConnectivitySnapshot {
		return SERVER_SNAPSHOT;
	},
	markOffline(reason?: string) {
		transition("offline", reason);
	},
	/**
	 * Set when `health.ping` fails with a **non–connectivity** error (e.g. 4xx/5xx
	 * other than 502/503/504). The browser may still be online; we keep the queue
	 * and backoff retry instead of forcing full offline.
	 */
	markDegraded(reason?: string) {
		transition("degraded", reason);
	},
	markReconnecting(reason?: string) {
		transition("reconnecting", reason);
	},
	markOnline() {
		transition("online");
	},
	setNextRetryAtMs(nextRetryAtMs: number | undefined) {
		setState({ ...state, nextRetryAtMs });
	},
	requestReconnectNow() {
		setState({
			...state,
			reconnectKick: state.reconnectKick + 1,
		});
	},
};
